import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as TasksIcon
} from '@mui/icons-material';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { fallbackColorForUid } from '../../utils/userColors';
import { isHiddenRole } from '../../utils/roles';
import {
  TASK_COLUMNS,
  TASK_VISIBILITY,
  NO_CLIENT,
  DEFAULT_TASK_FILTERS,
  normalizeTaskFilters
} from '../../utils/taskConstants';
import TaskCard from './TaskCard';
import TaskColumn from './TaskColumn';
import TaskDialog from './TaskDialog';
import ClientAutocomplete from './ClientAutocomplete';

const FilterLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: '0.75rem',
      color: 'text.secondary',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      whiteSpace: 'nowrap'
    }}
  >
    {children}
  </Typography>
);

export default function Tasks() {
  const { currentUser, userProfile } = useAuth();
  const [tasksById, setTasksById] = useState({ company: {}, private: {} });
  const [usersByUid, setUsersByUid] = useState({});
  const [activeUsers, setActiveUsers] = useState([]);
  const [clientsById, setClientsById] = useState({});
  const [activeClients, setActiveClients] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_TASK_FILTERS);
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [defaultColumnKey, setDefaultColumnKey] = useState(TASK_COLUMNS[0].key);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const userDocRefRef = useRef(null);
  // Optimistic column overrides applied while a drag's Firestore write is in flight
  const [columnOverrides, setColumnOverrides] = useState({});
  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // --- Load saved filters (before first board render) ---
  useEffect(() => {
    let cancelled = false;
    const loadFilters = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, 'users'), where('uid', '==', currentUser.uid))
        );
        if (!snapshot.empty) {
          userDocRefRef.current = snapshot.docs[0].ref;
          if (!cancelled) setFilters(normalizeTaskFilters(snapshot.docs[0].data().taskFilters));
        }
      } catch (error) {
        console.error('Error loading task filters:', error);
      } finally {
        if (!cancelled) setFiltersLoaded(true);
      }
    };
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [currentUser.uid]);

  const updateFilters = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    if (userDocRefRef.current) {
      updateDoc(userDocRefRef.current, { taskFilters: next }).catch((error) =>
        console.error('Error saving task filters:', error)
      );
    }
  };

  // --- Users & clients (one-shot) ---
  useEffect(() => {
    const load = async () => {
      try {
        const [usersSnap, clientsSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'clients'))
        ]);
        const byUid = {};
        const active = [];
        usersSnap.forEach((docSnap) => {
          const data = docSnap.data();
          if (!data.uid) return;
          const user = {
            uid: data.uid,
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email,
            color: data.color || fallbackColorForUid(data.uid)
          };
          byUid[data.uid] = user;
          if (data.status === 'Active' && !isHiddenRole(data.role)) active.push(user);
        });
        setUsersByUid(byUid);
        setActiveUsers(active);

        const cById = {};
        const cActive = [];
        clientsSnap.forEach((docSnap) => {
          const data = docSnap.data();
          const client = { id: docSnap.id, clientName: data.clientName || '' };
          cById[docSnap.id] = client;
          if (data.status === 'Active') cActive.push(client);
        });
        cActive.sort((a, b) => a.clientName.localeCompare(b.clientName));
        setClientsById(cById);
        setActiveClients(cActive);
      } catch (error) {
        console.error('Error loading users/clients:', error);
      }
    };
    load();
  }, []);

  // --- Task subscriptions: two merged listeners so other users' private
  // tasks never reach the browser (PRD §3) ---
  useEffect(() => {
    const applySlice = (slice, snapshot) => {
      const docsById = {};
      snapshot.forEach((docSnap) => {
        docsById[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
      });
      setTasksById((prev) => ({ ...prev, [slice]: docsById }));
      setTasksLoaded(true);
    };

    const unsubCompany = onSnapshot(
      query(collection(db, 'tasks'), where('visibility', '==', TASK_VISIBILITY.COMPANY)),
      (snapshot) => applySlice('company', snapshot),
      (error) => console.error('Error loading company tasks:', error)
    );
    const unsubPrivate = onSnapshot(
      query(
        collection(db, 'tasks'),
        where('visibility', '==', TASK_VISIBILITY.PRIVATE),
        where('creatorUid', '==', currentUser.uid)
      ),
      (snapshot) => applySlice('private', snapshot),
      (error) => console.error('Error loading private tasks:', error)
    );
    return () => {
      unsubCompany();
      unsubPrivate();
    };
  }, [currentUser.uid]);

  const allTasks = useMemo(() => {
    const merged = { ...tasksById.company, ...tasksById.private };
    return Object.values(merged).map((task) =>
      columnOverrides[task.id] ? { ...task, columnKey: columnOverrides[task.id] } : task
    );
  }, [tasksById, columnOverrides]);

  // --- Filtering (AND-ed, PRD §5) ---
  const filteredTasks = useMemo(() => {
    return allTasks.filter((task) => {
      if (filters.status === 'incomplete' && task.completed) return false;
      if (filters.status === 'completed' && !task.completed) return false;
      if (filters.visibility !== 'all' && task.visibility !== filters.visibility) return false;
      if (filters.assignee === 'me' && task.assigneeUid !== currentUser.uid) return false;
      if (filters.assignee === 'unassigned' && task.assigneeUid) return false;
      if (filters.client === NO_CLIENT && task.clientId) return false;
      if (filters.client && filters.client !== NO_CLIENT && task.clientId !== filters.client)
        return false;
      return true;
    });
  }, [allTasks, filters, currentUser.uid]);

  const commentUser = {
    uid: currentUser?.uid,
    name:
      usersByUid[currentUser?.uid]?.name ||
      `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() ||
      currentUser?.email ||
      'Unknown user',
    color: usersByUid[currentUser?.uid]?.color || fallbackColorForUid(currentUser?.uid)
  };

  // --- Dialog / CRUD ---
  const openCreateDialog = (columnKey = TASK_COLUMNS[0].key) => {
    setEditingTask(null);
    setDefaultColumnKey(columnKey);
    setDialogOpen(true);
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleSave = async (payload, task) => {
    try {
      setSaving(true);
      if (task) {
        await updateDoc(doc(db, 'tasks', task.id), {
          ...payload,
          updatedAt: serverTimestamp()
        });
        setSnackbar({ open: true, message: 'Task updated', severity: 'success' });
      } else {
        await addDoc(collection(db, 'tasks'), {
          ...payload,
          columnKey: defaultColumnKey,
          completed: false,
          completedAt: null,
          commentCount: 0,
          creatorUid: currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setSnackbar({ open: true, message: 'Task created', severity: 'success' });
      }
      setDialogOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      setSnackbar({ open: true, message: 'Error saving task', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      // Firestore doesn't cascade subcollection deletes — remove comments first
      try {
        const commentsSnap = await getDocs(collection(db, 'tasks', task.id, 'comments'));
        if (!commentsSnap.empty) {
          const batch = writeBatch(db);
          commentsSnap.forEach((c) => batch.delete(c.ref));
          await batch.commit();
        }
      } catch (error) {
        console.error('Error deleting task comments:', error);
      }
      await deleteDoc(doc(db, 'tasks', task.id));
      setDialogOpen(false);
      setEditingTask(null);
      setSnackbar({ open: true, message: 'Task deleted', severity: 'info' });
    } catch (error) {
      console.error('Error deleting task:', error);
      setSnackbar({ open: true, message: 'Error deleting task', severity: 'error' });
    }
  };

  const handleDragStart = ({ active }) => {
    setActiveTask(allTasks.find((t) => t.id === active.id) || null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    const task = allTasks.find((t) => t.id === active.id);
    if (!task || task.completed || task.columnKey === over.id) return;

    setColumnOverrides((prev) => ({ ...prev, [task.id]: over.id }));
    try {
      await updateDoc(doc(db, 'tasks', task.id), {
        columnKey: over.id,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error moving task:', error);
      setSnackbar({ open: true, message: 'Error moving task', severity: 'error' });
    } finally {
      setActiveTask(null);
      setColumnOverrides((prev) => {
        const next = { ...prev };
        delete next[task.id];
        return next;
      });
    }
  };

  const ready = filtersLoaded && tasksLoaded;

  return (
    <Container
      maxWidth={false}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0 }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TasksIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Tasks
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog()}>
          Add Task
        </Button>
      </Box>

      {/* Filter bar */}
      <Paper sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, mb: 2, flexShrink: 0 }}>
        <FilterLabel>Status</FilterLabel>
        <Select
          size="small"
          value={filters.status}
          onChange={(e) => updateFilters({ status: e.target.value })}
        >
          <MenuItem value="incomplete">Incomplete</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </Select>
        <Divider orientation="vertical" flexItem />
        <FilterLabel>Visibility</FilterLabel>
        <Select
          size="small"
          value={filters.visibility}
          onChange={(e) => updateFilters({ visibility: e.target.value })}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="company">Company</MenuItem>
          <MenuItem value="private">Private</MenuItem>
        </Select>
        <Divider orientation="vertical" flexItem />
        <FilterLabel>Assignee</FilterLabel>
        <Select
          size="small"
          value={filters.assignee}
          onChange={(e) => updateFilters({ assignee: e.target.value })}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="me">Me</MenuItem>
          <MenuItem value="unassigned">Unassigned</MenuItem>
        </Select>
        <Divider orientation="vertical" flexItem />
        <FilterLabel>Client</FilterLabel>
        <ClientAutocomplete
          clients={activeClients}
          value={filters.client}
          onChange={(next) => updateFilters({ client: next })}
          includeNoneOption
          placeholder="All clients"
          sx={{ width: 220 }}
        />
      </Paper>

      {/* Board */}
      {!ready ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, minmax(240px, 1fr))',
              gap: 2,
              flex: 1,
              minHeight: 0,
              overflowX: 'auto',
              pb: 1
            }}
          >
            {TASK_COLUMNS.map((column) => (
              <TaskColumn
                key={column.key}
                column={column}
                tasks={filteredTasks.filter((t) => t.columnKey === column.key)}
                statusFilter={filters.status}
                usersByUid={usersByUid}
                clientsById={clientsById}
                onOpenTask={openEditDialog}
                onAddTask={openCreateDialog}
              />
            ))}
          </Box>
          <DragOverlay dropAnimation={null}>
            {activeTask ? (
              <TaskCard
                task={activeTask}
                assignee={activeTask.assigneeUid ? usersByUid[activeTask.assigneeUid] : null}
                clientName={activeTask.clientId ? clientsById[activeTask.clientId]?.clientName : null}
                onClick={() => {}}
                overlay
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Task dialog */}
      <TaskDialog
        open={dialogOpen}
        task={editingTask}
        defaultColumnKey={defaultColumnKey}
        activeUsers={activeUsers}
        usersByUid={usersByUid}
        activeClients={activeClients}
        currentUser={commentUser}
        onClose={() => {
          setDialogOpen(false);
          setEditingTask(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
        saving={saving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
